import { useEffect, useRef } from "react";

import { snackbar } from "./snackbarStore";
import { SnackbarItem } from "./types";

type Timer = {
  /** Milliseconds still to run once the timer resumes. */
  remaining: number;
  startedAt: number;
  timeoutId?: ReturnType<typeof setTimeout>;
};

/**
 * Auto-close timers for the whole stack. Pauses while the stack is hovered or
 * focused, otherwise the expanded list dismisses itself while being read.
 */
export function useSnackbarTimers(items: SnackbarItem[], isPaused: boolean) {
  const timersRef = useRef(new Map<string, Timer>());

  useEffect(() => {
    const timers = timersRef.current;
    const liveIds = new Set(items.map((item) => item.id));

    timers.forEach((timer, id) => {
      if (liveIds.has(id)) return;
      if (timer.timeoutId) clearTimeout(timer.timeoutId);
      timers.delete(id);
    });

    items.forEach((item) => {
      if (item.autoHideDuration === null) return;
      if (timers.has(item.id)) return;
      timers.set(item.id, {
        remaining: item.autoHideDuration,
        startedAt: Date.now(),
      });
    });

    timers.forEach((timer, id) => {
      if (isPaused) {
        if (!timer.timeoutId) return;
        clearTimeout(timer.timeoutId);
        timer.timeoutId = undefined;
        timer.remaining = Math.max(
          0,
          timer.remaining - (Date.now() - timer.startedAt)
        );
        return;
      }

      if (timer.timeoutId) return;
      timer.startedAt = Date.now();
      timer.timeoutId = setTimeout(() => {
        timers.delete(id);
        snackbar.dismiss(id);
      }, timer.remaining);
    });
  }, [items, isPaused]);

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((timer) => {
        if (timer.timeoutId) clearTimeout(timer.timeoutId);
      });
      timers.clear();
    };
  }, []);
}
